export interface CopyToClipboardOptions {
  fallback?: (value: string) => void | Promise<void>;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
}

export interface ReadFromClipboardOptions {
  fallback?: () => string | Promise<string>;
  onError?: (error: unknown) => void;
  onSuccess?: (value: string) => void;
}

const getClipboard = (): Clipboard | null => (navigator === undefined ? null : (navigator.clipboard ?? null));

export async function copyToClipboard(value: string, options: CopyToClipboardOptions = {}): Promise<boolean> {
  const { onSuccess, onError, fallback } = options;
  const clipboard = getClipboard();

  const runOnSuccess = () => {
    onSuccess?.();
  };

  const runOnError = (error: unknown) => {
    onError?.(error);
  };

  if (!clipboard || typeof clipboard.writeText !== 'function') {
    try {
      if (fallback) {
        await fallback(value);
        runOnSuccess();
        return true;
      }
      runOnError(new Error('Clipboard API is unavailable in this environment'));
      return false;
    } catch (error) {
      runOnError(error);
      return false;
    }
  }

  try {
    await clipboard.writeText(value);
    runOnSuccess();
    return true;
  } catch (error) {
    try {
      if (fallback) {
        await fallback(value);
        runOnSuccess();
        return true;
      }
      runOnError(error);
      return false;
    } catch (fallbackError) {
      runOnError(fallbackError);
      return false;
    }
  }
}

export async function readFromClipboard(options: ReadFromClipboardOptions = {}): Promise<string | null> {
  const { onSuccess, onError, fallback } = options;
  const clipboard = getClipboard();

  const runOnSuccess = (value: string) => {
    onSuccess?.(value);
  };

  const runOnError = (error: unknown) => {
    onError?.(error);
  };

  if (!clipboard || typeof clipboard.readText !== 'function') {
    try {
      if (!fallback) {
        throw new Error('Clipboard read is unavailable in this environment');
      }
      const value = await fallback();
      runOnSuccess(value);
      return value;
    } catch (error) {
      runOnError(error);
      return null;
    }
  }

  try {
    const value = await clipboard.readText();
    runOnSuccess(value);
    return value;
  } catch (error) {
    if (fallback) {
      try {
        const value = await fallback();
        runOnSuccess(value);
        return value;
      } catch (fallbackError) {
        runOnError(fallbackError);
        return null;
      }
    }
    runOnError(error);
    return null;
  }
}
